import { User } from '../types';

/**
 * RBAC dashboard — helper e mappa route→permessi.
 *
 * Le chiavi rispecchiano AdminPermissions.CATALOG del BE (unica fonte di
 * verità: il BE le valida, qui servono solo per decidere cosa mostrare).
 * Il gating FE è UX, non sicurezza: anche bypassandolo, le regole di
 * SecurityConfig rifiutano le chiamate fuori permesso.
 */

export const PERM = {
  ALL: 'ALL',
  DASHBOARD_VIEW: 'dashboard.view',
  USERS_VIEW: 'users.view',
  USERS_MANAGE: 'users.manage',
  USERS_DELETE: 'users.delete',
  ADMINS_MANAGE: 'admins.manage',
  SUBSCRIPTIONS_VIEW: 'subscriptions.view',
  SUBSCRIPTIONS_MANAGE: 'subscriptions.manage',
  SUBSCRIPTIONS_HISTORY: 'subscriptions.history',
  SUBSCRIPTIONS_EROGAZIONE: 'subscriptions.erogazione',
  STATS_VIEW: 'stats.view',
  PAYMENTS_VIEW: 'payments.view',
  COMMISSIONS_VIEW: 'commissions.view',
  COMMISSIONS_PAYOUTS: 'commissions.payouts',
  COMMISSIONS_RATES: 'commissions.rates',
  COMMISSIONS_NETWORK: 'commissions.network',
  COINS_VIEW: 'coins.view',
  COINS_MANAGE: 'coins.manage',
  PACKAGES_MANAGE: 'packages.manage',
  NEWSLETTER_VIEW: 'newsletter.view',
  NEWSLETTER_MANAGE: 'newsletter.manage',
  BLOG_MANAGE: 'blog.manage',
  CONFIG_MANAGE: 'config.manage',
  FIC_MANAGE: 'fic.manage',
  MARKETING_MANAGE: 'marketing.manage',
  CONTENT_MANAGE: 'content.manage',
  TRANSLATIONS_MANAGE: 'translations.manage',
  LANGUAGES_MANAGE: 'languages.manage',
} as const;

export type Permission = (typeof PERM)[keyof typeof PERM];

/** True se l'utente ha almeno uno dei permessi indicati (ALL passa sempre). */
export function can(user: User | null | undefined, ...anyOf: string[]): boolean {
  const perms = user?.permissions;
  if (!perms || perms.length === 0) return false;
  if (perms.includes(PERM.ALL)) return true;
  return anyOf.some((p) => perms.includes(p));
}

/**
 * Permessi che rendono visibile ogni route (any-of). Stessa mappa per
 * sidebar, guard delle route e redirect post-login: se divergessero, una
 * voce di menu potrebbe portare a una pagina vietata o viceversa.
 */
export const ROUTE_PERMISSIONS: Record<string, string[]> = {
  '/dashboard': [PERM.DASHBOARD_VIEW],
  '/users': [PERM.USERS_VIEW, PERM.USERS_MANAGE, PERM.ADMINS_MANAGE],
  '/subscriptions': [
    PERM.SUBSCRIPTIONS_VIEW,
    PERM.SUBSCRIPTIONS_HISTORY,
    PERM.SUBSCRIPTIONS_EROGAZIONE,
  ],
  '/subscription-stats': [PERM.STATS_VIEW],
  '/stripe': [PERM.PAYMENTS_VIEW],
  '/commissions': [
    PERM.COMMISSIONS_VIEW,
    PERM.COMMISSIONS_PAYOUTS,
    PERM.COMMISSIONS_RATES,
    PERM.COMMISSIONS_NETWORK,
  ],
  '/coins': [PERM.COINS_VIEW, PERM.COINS_MANAGE],
  '/packages': [PERM.PACKAGES_MANAGE],
  '/newsletter': [PERM.NEWSLETTER_VIEW, PERM.NEWSLETTER_MANAGE],
  // Composer (nuova campagna / modifica): serve il manage, la view non basta
  '/newsletter/new': [PERM.NEWSLETTER_MANAGE],
  '/blog': [PERM.BLOG_MANAGE],
  // La pagina è visibile con QUALSIASI tab consentito; il gating per-tab
  // vive dentro PlatformConfig.
  '/config': [PERM.CONFIG_MANAGE, PERM.FIC_MANAGE, PERM.MARKETING_MANAGE, PERM.CONTENT_MANAGE],
  '/languages': [PERM.TRANSLATIONS_MANAGE, PERM.LANGUAGES_MANAGE],
  '/legal': [PERM.CONTENT_MANAGE],
};

/** Ordine di preferenza per l'atterraggio post-login. */
const LANDING_ORDER = [
  '/dashboard',
  '/users',
  '/subscriptions',
  '/subscription-stats',
  '/stripe',
  '/commissions',
  '/coins',
  '/packages',
  '/newsletter',
  '/blog',
  '/languages',
  '/legal',
  '/config',
];

/**
 * Prima route consentita all'utente, o null se non ne ha nessuna (permessi
 * vuoti/non ancora caricati). Il chiamante DEVE gestire il null con una
 * schermata dedicata: un fallback su una route vietata creerebbe un loop di
 * redirect.
 */
export function firstAllowedRoute(user: User | null | undefined): string | null {
  for (const route of LANDING_ORDER) {
    if (can(user, ...(ROUTE_PERMISSIONS[route] ?? []))) return route;
  }
  return null;
}
